import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerCallFlowExtraDataT } from '../PowerCallFlow';
import { DialInput as ActionModal } from '../modals/dialInput';

export interface Announcement {
  file: {
    author: string;
    filename: string;
    url: string;
  };
}

type Payload = {
  announcement: Announcement;
  end_key: null | '*' | '#';
  max_digits: null | number;
  next_node_id: string;
  variable_id: string;
};

export type FlowCallNodeDialInput = NodeT<FlowNodeTypes.DIAL_INPUT, Payload>;

type ScopeVariables = {
  grants: string[];
  needs: string[];
};

const portTranslator = (node: FlowCallNodeDialInput): RegisterPortT[] => [
  {
    id: uuid(),
    type: 'out',
    label: 'arrow',
    connection: node.payload.next_node_id || uuid(),
  },
];

const translateScopeVariables = (
  node: FlowCallNodeDialInput,
): ScopeVariables => {
  const scopeVariables: ScopeVariables = {
    grants: [],
    needs: [],
  };

  if (node.payload.variable_id) {
    scopeVariables.grants.push(node.payload.variable_id);
  }

  return scopeVariables;
};

export class DialInputNode extends NodeTypeComponent<
  FlowCallNodeDialInput,
  PowerCallFlowExtraDataT
>() {
  static icon: IconNames = 'phone';
  static label = 'Dial input';
  static helpText =
    'Allow customers to give input with their dial pad during a call (e.g. a PIN, ZIP code, or phone number).';
  static color = flowNodeColor.orange;
  static translatePayload = formatPortsHelper(portTranslator);
  static translateScopeVariables = translateScopeVariables;
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.DIAL_INPUT };
  static updateConnection = (
    node: FlowCallNodeDialInput,
    _port: InternalPortT,
    newConnection: string,
  ): FlowCallNodeDialInput => {
    const payload = { ...node.payload };
    payload.next_node_id = newConnection;

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    return (
      <FlowNode
        color={flowNodeColor.orange}
        icon={DialInputNode.icon}
        label={DialInputNode.label}
        text={
          <div>
            Variable <strong>{this.props.node.payload.variable_id}</strong>
          </div>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
